import { useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquareText, ChevronRight, Zap, HardHat } from 'lucide-react';

const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

// Encart "Nouveaux messages sur vos dossiers" — pour tous les utilisateurs.
// Lit la synthèse déjà chargée par le contexte (aucune requête supplémentaire = zéro ralentissement).
export default function WallUnreadWidget() {
    const navigate = useNavigate();
    const { wallUnreadItems, wallUnreadMessages } = useAuth();

    // 🔒 Ordre client STABLE : le contexte auth refresh la liste toutes les 60s ce qui
    // peut réordonner les items (ex: un nouveau message fait remonter un dossier).
    // Si l'user est en train de cliquer, le reorder déplace la cible et il clique à côté.
    // On mémorise l'ordre de la 1ère apparition de chaque item pour figer les positions.
    // Les nouveaux items apparaissent en fin de liste (ne perturbent pas ce que l'user vise).
    const orderMapRef = useRef(new Map());
    const orderedItems = useMemo(() => {
        if (!wallUnreadItems || wallUnreadItems.length === 0) return [];
        // Assigne un rang stable à chaque item non-connu, en préservant les rangs existants
        const map = orderMapRef.current;
        let nextRank = map.size;
        wallUnreadItems.forEach((it) => {
            const key = `${it.prospect_id}-${it.module}`;
            if (!map.has(key)) map.set(key, nextRank++);
        });
        // Nettoie les clés obsolètes (items disparus définitivement)
        const activeKeys = new Set(wallUnreadItems.map((it) => `${it.prospect_id}-${it.module}`));
        Array.from(map.keys()).forEach((k) => { if (!activeKeys.has(k)) map.delete(k); });
        // Tri stable sur le rang mémorisé
        return [...wallUnreadItems].sort((a, b) => {
            const ka = `${a.prospect_id}-${a.module}`;
            const kb = `${b.prospect_id}-${b.module}`;
            return (map.get(ka) ?? 0) - (map.get(kb) ?? 0);
        });
    }, [wallUnreadItems]);

    if (!wallUnreadItems || wallUnreadItems.length === 0) return null;

    return (
        <Card data-testid="wall-unread-widget" className="border-purple-200 bg-gradient-to-br from-purple-50/60 to-white">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquareText className="w-5 h-5 text-purple-600" />
                    Nouveaux messages sur vos dossiers
                    <Badge className="ml-1 bg-purple-600 text-white">{wallUnreadMessages}</Badge>
                    <button
                        type="button"
                        onClick={() => navigate('/prospects?view=unread')}
                        className="ml-auto text-xs font-semibold text-purple-700 hover:text-purple-900 underline"
                        data-testid="wall-unread-see-all"
                    >
                        Voir tous
                    </button>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <div className="max-h-80 overflow-y-auto divide-y divide-slate-100" style={{ overflowAnchor: 'auto' }}>
                    {orderedItems.map((it) => {
                        const isCh = it.module === 'chantier';
                        const Icon = isCh ? HardHat : Zap;
                        return (
                            <button
                                key={`${it.prospect_id}-${it.module}`}
                                type="button"
                                onClick={() => {
                                    // Deep-link vers le message ciblé (comme depuis la notif) : le mur
                                    // s'ouvre déjà scrollé/highlighté sur le dernier message non lu.
                                    const noteParam = it.last_note_id ? `&note=${it.last_note_id}` : '';
                                    navigate(`/prospects/${it.prospect_id}?wall=${it.module}${noteParam}`);
                                }}
                                data-testid={`wall-unread-item-${it.prospect_id}-${it.module}`}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50/70 transition-colors"
                            >
                                <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${isCh ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-slate-800 truncate">{it.prospect_name}</p>
                                        <Badge variant="outline" className="text-[10px] shrink-0">{it.module_label}</Badge>
                                    </div>
                                    <p className="text-xs text-slate-500 truncate">
                                        {it.last_author_name ? `${it.last_author_name} · ` : ''}{timeAgo(it.last_message_at)}
                                    </p>
                                </div>
                                <Badge className="bg-purple-600 text-white shrink-0">{it.unread_count}</Badge>
                                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                            </button>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
